import { useCallback, useEffect, useMemo, useState } from 'react';
import { nomDuChantier } from '../../lib/nom-chantier';
import { bonsAchatApi } from '../../api/resources';
import { BonAchatA4 } from '../../components/BonAchatA4.jsx';
import { Shell } from '../../components/layout/Shell.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { _inp } from '../../components/ui/Inputs.jsx';
import {
  bonVersDto,
  bonVide,
  chantiersEnCours,
  dtoVersBon,
  fournisseurDuCarnet,
  fournisseursConnus,
  ligneVide,
  manqueAuBon,
  nomEmploye,
  estPartiel,
  quantiteLisible,
  resteAAcheter,
  statutLisible,
} from '../../lib/bons-achat';
import { dateSuisse } from '../../lib/dates';
import { tr, trf } from '../../lib/i18n';
import { aQuitte } from '../../lib/personnel';
import { C } from '../../theme/palette';

/** Unités proposées pour une ligne : les plus courantes au comptoir. */
const UNITES = ['pce', 'sac', 'm', 'm²', 'm³', 'kg', 'l', 'rlx', 'bte', 'pal', 'paq', 'lot'];

const titreChamp = {
  fontSize: 10,
  fontWeight: 800,
  color: C.muted,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  marginBottom: 5,
  display: 'block',
};

/** « 07:42 » si c'est aujourd'hui, sinon la date. */
function quand(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const auj = new Date();
  const memeJour = d.toDateString() === auj.toDateString();
  const hm = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  return memeJour ? hm : dateSuisse(d.toISOString().slice(0, 10)) + ' ' + hm;
}

/** Où en est le bon, en une ligne pour le bureau. */
function suivi(b) {
  const prenom = (b.employeNom || '').split(' ')[0];
  if (b.statut === 'ETABLI') return tr('Pas encore envoyé');
  if (b.statut === 'ACHETE') return '📷 ' + tr('ticket reçu') + ' ' + quand(b.acheteLe);
  const envoi = tr('Envoyé') + ' ' + quand(b.envoyeLe);
  return b.statut === 'VU'
    ? envoi + ' · 👁 ' + tr('vu par') + ' ' + prenom + ' ' + quand(b.vuLe)
    : envoi + ' · ' + tr('pas encore vu');
}

function Pastille({ bon }) {
  const statut = bon.statut;
  const achete = statut === 'ACHETE';
  const attente = statut === 'ETABLI';
  return (
    <span
      style={{
        background: achete ? '#EDFAF4' : attente ? '#EEF2F8' : '#FFFBEB',
        color: achete ? '#0F6B3A' : attente ? '#1E2D4A' : '#92400E',
        borderRadius: 999,
        padding: '4px 9px',
        fontSize: 11,
        fontWeight: 800,
        whiteSpace: 'nowrap',
        alignSelf: 'flex-start',
      }}
    >
      {tr(statutLisible(bon))}
    </span>
  );
}

/**
 * « 🧾 Bons d'achat » : le bureau envoie un employé chercher du matériel chez
 * un fournisseur, pour un chantier en cours, sans prix. Au magasin, le bon
 * remplit le stock : il n'a pas de chantier.
 *
 * <p>Patron, technicien, secrétaire et comptable l'établissent. « 📲 Envoyer
 * à … » le dépose dans l'espace de l'employé, à côté de ses heures et de son
 * planning ; « 🖨 Imprimer / PDF » sort la feuille A4. La liste dit, pour
 * chaque bon, s'il a été vu et si le ticket est arrivé.
 *
 * @param {object} props propriétés de la page
 * @param {object} [props.ouvrir] { chantierId } pour un bon neuf sur ce chantier
 * @returns {import('react').ReactElement} la page
 */
export function BonsAchatPage({
  onNav,
  soc,
  chantiers,
  employes,
  ouvrir,
  stock,
  estMixte,
  univers,
  setUnivers,
}) {
  const mag = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
  const [bons, setBons] = useState(null);
  const [erreur, setErreur] = useState('');
  const [filtre, setFiltre] = useState('tous');
  // Le formulaire : null fermé, sinon le bon en cours (neuf ou repris).
  const [form, setForm] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState('');
  const [feuille, setFeuille] = useState(null);
  // Le carnet des fournisseurs : nom, adresse, téléphone, choisis d'un toucher.
  const [carnet, setCarnet] = useState([]);
  const [carnetOuvert, setCarnetOuvert] = useState(false);
  const [fiche, setFiche] = useState(null);
  const chargeCarnet = useCallback(() => {
    bonsAchatApi
      .fournisseurs()
      .then((l) => setCarnet(l || []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    chargeCarnet();
  }, [chargeCarnet]);

  const charge = useCallback(() => {
    bonsAchatApi
      .liste()
      .then((l) => {
        setBons(l || []);
        setErreur('');
      })
      .catch((e) => setErreur(e?.message || tr('Les bons d’achat n’ont pas pu être lus.')));
  }, []);
  useEffect(() => {
    charge();
  }, [charge]);

  // Depuis un chantier : le bon s'ouvre déjà sur lui.
  useEffect(() => {
    if (ouvrir?.chantierId) setForm(bonVide({ chantierId: ouvrir.chantierId }));
  }, [ouvrir]);

  const equipe = useMemo(
    () =>
      (employes || [])
        .filter((e) => typeof e.id === 'number' && !aQuitte(e))
        .sort((a, b) => nomEmploye(a).localeCompare(nomEmploye(b))),
    [employes],
  );
  const ouverts = useMemo(() => {
    const enCours = chantiersEnCours(chantiers);
    // Un bon repris garde son chantier, même s'il n'est plus en cours.
    const actuel = form?.chantierId
      ? (chantiers || []).find((c) => String(c.id) === String(form.chantierId))
      : null;
    return actuel && !enCours.includes(actuel) ? [actuel, ...enCours] : enCours;
  }, [chantiers, form?.chantierId]);
  // Un seul carnet : les fournisseurs notés dans Dépenses se proposent aussi
  // ici. Deux carnets séparés obligeaient à tout saisir deux fois.
  const deDepenses = useMemo(
    () =>
      (Array.isArray(soc?.fournisseurs) ? soc.fournisseurs : [])
        .filter((f) => f?.nom && !fournisseurDuCarnet(carnet, f.nom))
        .map((f) => ({ nom: f.nom, adresse: f.adresse || '' })),
    [soc?.fournisseurs, carnet],
  );
  const connus = useMemo(
    () => [
      ...carnet.map((f) => ({ nom: f.nom, adresse: f.adresse || '' })),
      ...deDepenses,
      ...fournisseursConnus(bons).filter(
        (f) =>
          !fournisseurDuCarnet(carnet, f.nom) &&
          !deDepenses.some((d) => d.nom.toLowerCase() === f.nom.toLowerCase()),
      ),
    ],
    [bons, carnet, deDepenses],
  );
  const chantierChoisi = (chantiers || []).find((c) => String(c.id) === String(form?.chantierId));
  const visibles = (bons || []).filter((b) =>
    filtre === 'acheter'
      ? resteAAcheter(b.statut) || b.statut === 'ETABLI'
      : filtre === 'achetes'
        ? b.statut === 'ACHETE'
        : true,
  );
  const employeChoisi = equipe.find((e) => String(e.id) === String(form?.employeId));
  const prenomChoisi = employeChoisi ? employeChoisi.prenom || employeChoisi.nom : '';

  const maj = (champ) => (e) => setForm((f) => ({ ...f, [champ]: e.target.value }));
  const majLigne = (i, champ, v) =>
    setForm((f) => ({
      ...f,
      lignes: f.lignes.map((l, k) => (k === i ? { ...l, [champ]: v } : l)),
    }));

  /** Enregistre le bon (neuf ou repris), puis l'envoie ou l'imprime. */
  const enregistre = async (suite) => {
    const manque = manqueAuBon(form, mag);
    if (manque) {
      setMessage(tr(manque));
      return;
    }
    setEnvoi(true);
    setMessage('');
    try {
      const corps = bonVersDto(form);
      let bon = form.id
        ? await bonsAchatApi.modifie(form.id, corps)
        : await bonsAchatApi.cree(corps);
      // Un fournisseur nouveau entre au carnet : son adresse se mettra seule
      // sur le bon suivant.
      if (!fournisseurDuCarnet(carnet, corps.fournisseur)) {
        bonsAchatApi
          .ajouteFournisseur({ nom: corps.fournisseur, adresse: corps.fournisseurAdresse })
          .then(chargeCarnet)
          .catch(() => {});
      }
      if (suite === 'envoyer') bon = await bonsAchatApi.envoie(bon.id);
      setForm(null);
      charge();
      if (suite === 'imprimer') setFeuille(bon);
      setMessage(
        suite === 'envoyer'
          ? '✓ ' + tr('Bon envoyé à') + ' ' + (bon.employeNom || '') + ' — ' + bon.numero
          : '✓ ' + tr('Bon enregistré') + ' — ' + bon.numero,
      );
    } catch (e) {
      setMessage(e?.message || tr('Le bon n’a pas pu être enregistré.'));
    } finally {
      setEnvoi(false);
    }
  };

  const envoie = async (b) => {
    try {
      await bonsAchatApi.envoie(b.id);
      setMessage('✓ ' + tr('Bon envoyé à') + ' ' + (b.employeNom || '') + ' — ' + b.numero);
      charge();
    } catch (e) {
      setMessage(e?.message || tr('Le bon n’a pas pu être envoyé.'));
    }
  };

  const supprime = async (b) => {
    if (!window.confirm(tr('Supprimer ce bon d’achat ?') + ' ' + b.numero)) return;
    try {
      await bonsAchatApi.supprime(b.id);
      charge();
    } catch (e) {
      setMessage(e?.message || tr('Le bon n’a pas pu être supprimé.'));
    }
  };

  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      back
      onBack={() => (form ? setForm(null) : onNav('plus'))}
      pageTitle={'🧾 ' + tr('Bons d’achat')}
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {message && (
          <div
            role="status"
            style={{
              background: message.startsWith('✓') ? '#EDFAF4' : C.redL,
              color: message.startsWith('✓') ? '#0F6B3A' : C.red,
              borderRadius: 10,
              padding: '9px 12px',
              fontSize: 12.5,
              fontWeight: 700,
            }}
          >
            {message}
          </div>
        )}

        {form ? (
          <>
            <div style={{ fontSize: 17, fontWeight: 900, color: C.ink }}>
              {form.id ? tr('Bon d’achat') + ' ' + form.numero : tr('Nouveau bon d’achat')}
            </div>
            {mag && (
              <datalist id="bon-articles-stock">
                {(stock || [])
                  .filter((a) => a?.nom)
                  .map((a) => (
                    <option key={a.id} value={a.nom} />
                  ))}
              </datalist>
            )}
            {mag ? (
              <Card style={{ padding: '12px 13px' }}>
                <div style={titreChamp}>🏪 {tr('Pour le magasin')}</div>
                <div style={{ fontSize: 12.5, color: C.muted }}>
                  {tr('La marchandise achetée va dans le stock du magasin.')}
                </div>
              </Card>
            ) : (
              <Card style={{ padding: '12px 13px' }}>
                <label style={titreChamp}>
                  🏗️ {tr('Chantier en cours')}
                  <select
                    aria-label={tr('Chantier en cours')}
                    value={form.chantierId}
                    onChange={maj('chantierId')}
                    style={{ ..._inp, marginTop: 4, textTransform: 'none' }}
                  >
                    <option value="">{tr('— choisir le chantier —')}</option>
                    {ouverts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {nomDuChantier(c, ouverts)}
                      </option>
                    ))}
                  </select>
                </label>
                {chantierChoisi && (chantierChoisi.adr || chantierChoisi.npa) && (
                  <div style={{ fontSize: 11.5, color: C.muted }}>
                    📍 {[chantierChoisi.adr, chantierChoisi.npa].filter(Boolean).join(', ')}
                  </div>
                )}
                {!ouverts.length && (
                  <div style={{ fontSize: 11.5, color: C.muted }}>
                    {tr('Aucun chantier en cours pour le moment.')}
                  </div>
                )}
              </Card>
            )}
            <Card style={{ padding: '12px 13px' }}>
              <label style={titreChamp}>
                👷 {tr('Employé qui va acheter')}
                <select
                  aria-label={tr('Employé qui va acheter')}
                  value={form.employeId}
                  onChange={maj('employeId')}
                  style={{ ..._inp, marginTop: 4, textTransform: 'none' }}
                >
                  <option value="">{tr('— choisir l’employé —')}</option>
                  {equipe.map((e) => (
                    <option key={e.id} value={e.id}>
                      {nomEmploye(e)}
                    </option>
                  ))}
                </select>
              </label>
            </Card>
            <Card style={{ padding: '12px 13px' }}>
              {connus.length > 0 && (
                <label style={titreChamp}>
                  🏪 {tr('Où acheter : mes fournisseurs')}
                  <select
                    aria-label={tr('Mes fournisseurs')}
                    value={fournisseurDuCarnet(connus, form.fournisseur)?.nom || ''}
                    onChange={(e) => {
                      const f = fournisseurDuCarnet(connus, e.target.value);
                      setForm((v) => ({
                        ...v,
                        fournisseur: f ? f.nom : '',
                        fournisseurAdresse: f ? f.adresse || '' : '',
                      }));
                    }}
                    style={{ ..._inp, marginTop: 4, textTransform: 'none' }}
                  >
                    <option value="">{tr('— choisir le fournisseur —')}</option>
                    {connus.map((f) => (
                      <option key={f.nom} value={f.nom}>
                        {f.nom}
                        {f.adresse ? ' — ' + f.adresse : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label style={titreChamp}>
                {connus.length > 0 ? tr('ou un autre fournisseur') : '🏪 ' + tr('Fournisseur')}
                <input
                  aria-label={tr('Fournisseur')}
                  value={form.fournisseur}
                  onChange={maj('fournisseur')}
                  placeholder={tr('ex. Matériaux Exemple SA')}
                  style={{ ..._inp, marginTop: 4, textTransform: 'none' }}
                />
              </label>
              <label style={{ ...titreChamp, marginBottom: 0 }}>
                {tr('Adresse du fournisseur (facultatif)')}
                <input
                  aria-label={tr('Adresse du fournisseur')}
                  value={form.fournisseurAdresse}
                  onChange={maj('fournisseurAdresse')}
                  placeholder={tr('ex. Route du Dépôt 5, 1228 Plan-les-Ouates')}
                  style={{ ..._inp, marginTop: 4, textTransform: 'none' }}
                />
              </label>
            </Card>
            <Card style={{ padding: '12px 13px' }}>
              <span style={titreChamp}>📦 {tr('Matériaux à acheter')}</span>
              {form.lignes.map((l, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) 62px 74px 30px',
                    gap: 6,
                    alignItems: 'center',
                    marginBottom: 6,
                  }}
                >
                  <input
                    aria-label={tr('Matériau') + ' ' + (i + 1)}
                    value={l.designation}
                    onChange={(e) => majLigne(i, 'designation', e.target.value)}
                    // Au magasin, le nom d'un article du stock : au ticket,
                    // sa quantité y entre.
                    list={mag ? 'bon-articles-stock' : undefined}
                    placeholder={tr('ex. Colle à carrelage 25 kg')}
                    style={{ ..._inp, textTransform: 'none' }}
                  />
                  <input
                    aria-label={tr('Quantité') + ' ' + (i + 1)}
                    inputMode="decimal"
                    value={l.quantite}
                    onChange={(e) => majLigne(i, 'quantite', e.target.value)}
                    placeholder={tr('Qté')}
                    style={{ ..._inp, textAlign: 'right' }}
                  />
                  <select
                    aria-label={tr('Unité') + ' ' + (i + 1)}
                    value={l.unite}
                    onChange={(e) => majLigne(i, 'unite', e.target.value)}
                    style={{ ..._inp, padding: '8px 4px' }}
                  >
                    {[...new Set([...(l.unite ? [l.unite] : []), ...UNITES])].map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    aria-label={tr('Retirer la ligne') + ' ' + (i + 1)}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        lignes:
                          f.lignes.length > 1 ? f.lignes.filter((_, k) => k !== i) : [ligneVide()],
                      }))
                    }
                    style={{
                      background: 'none',
                      border: 'none',
                      color: C.red,
                      fontSize: 18,
                      cursor: 'pointer',
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, lignes: [...f.lignes, ligneVide()] }))}
                style={{
                  width: '100%',
                  border: '1.5px dashed ' + C.green,
                  color: C.green,
                  background: '#EEF8F1',
                  borderRadius: 10,
                  padding: 9,
                  fontWeight: 800,
                  fontSize: 13,
                  cursor: 'pointer',
                  marginTop: 4,
                }}
              >
                ＋ {tr('ajouter un matériau')}
              </button>
            </Card>
            <Card style={{ padding: '12px 13px' }}>
              <label style={{ ...titreChamp, marginBottom: 0 }}>
                ✍️ {tr('Remarque (facultatif)')}
                <textarea
                  aria-label={tr('Remarque')}
                  rows={3}
                  value={form.remarque}
                  onChange={maj('remarque')}
                  placeholder={tr('ex. Prendre la livraison au dépôt avant 10h')}
                  style={{ ..._inp, marginTop: 4, textTransform: 'none', fontFamily: 'inherit' }}
                />
              </label>
            </Card>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Btn
                label={'🖨 ' + tr('Imprimer / PDF')}
                ghost
                full
                disabled={envoi}
                onClick={() => enregistre('imprimer')}
              />
              <Btn
                label={
                  envoi
                    ? tr('Envoi…')
                    : '📲 ' +
                      (prenomChoisi
                        ? tr('Envoyer à') + ' ' + prenomChoisi
                        : tr('Envoyer à l’employé'))
                }
                color={C.green}
                full
                disabled={envoi}
                onClick={() => enregistre('envoyer')}
              />
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.45 }}>
              {tr(
                '« Envoyer » dépose le bon dans l’espace de l’employé (là où il voit ses heures et son planning), avec une notification. Aucun prix n’y figure.',
              )}
            </div>
            <Btn label={tr('Annuler')} ghost full onClick={() => setForm(null)} />
          </>
        ) : (
          <>
            <Btn
              label={'＋ ' + tr('Nouveau bon d’achat')}
              color={C.green}
              full
              onClick={() => {
                setMessage('');
                setForm(bonVide());
              }}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                ['tous', 'Tous'],
                ['acheter', 'À acheter'],
                ['achetes', 'Achetés'],
              ].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFiltre(k)}
                  style={{
                    background: filtre === k ? C.navy : '#EEF2F8',
                    color: filtre === k ? '#fff' : '#1E2D4A',
                    border: 'none',
                    borderRadius: 999,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  {tr(l)}
                </button>
              ))}
            </div>
            {erreur && <div style={{ color: C.red, fontSize: 12 }}>{erreur}</div>}
            {bons === null && !erreur && (
              <div style={{ color: C.muted, fontSize: 12 }}>{tr('Chargement…')}</div>
            )}
            {bons && visibles.length === 0 && (
              <Card style={{ padding: '12px 13px' }}>
                <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>
                  {tr(
                    'Aucun bon d’achat. Touchez « Nouveau bon d’achat » pour envoyer un employé chercher du matériel chez un fournisseur.',
                  )}
                </div>
              </Card>
            )}
            <Card style={{ padding: '12px 13px' }}>
              <button
                type="button"
                onClick={() => setCarnetOuvert((o) => !o)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: 13.5,
                  fontWeight: 900,
                  color: C.ink,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <span>
                  🏪 {tr('Mes fournisseurs')} ({carnet.length})
                </span>
                <span>{carnetOuvert ? '▲' : '▼'}</span>
              </button>
              {carnetOuvert && (
                <div style={{ marginTop: 10 }}>
                  {carnet.map((f) => (
                    <div
                      key={f.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '7px 0',
                        borderBottom: '1px dashed ' + C.border,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 800 }}>{f.nom}</div>
                        <div style={{ fontSize: 11.5, color: C.muted }}>
                          {[f.adresse, f.telephone].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Btn sm ghost label="✎" onClick={() => setFiche({ ...f })} />
                        <Btn
                          sm
                          ghost
                          danger
                          label="🗑"
                          onClick={async () => {
                            if (
                              !window.confirm(
                                tr('Retirer ce fournisseur du carnet ?') + ' ' + f.nom,
                              )
                            )
                              return;
                            await bonsAchatApi.supprimeFournisseur(f.id).catch(() => {});
                            chargeCarnet();
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  {fiche ? (
                    <div
                      style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}
                    >
                      <input
                        aria-label={tr('Nom du fournisseur')}
                        value={fiche.nom}
                        onChange={(e) => setFiche((v) => ({ ...v, nom: e.target.value }))}
                        placeholder={tr('ex. Matériaux Exemple SA')}
                        style={{ ..._inp, textTransform: 'none' }}
                      />
                      <input
                        aria-label={tr('Adresse du fournisseur')}
                        value={fiche.adresse || ''}
                        onChange={(e) => setFiche((v) => ({ ...v, adresse: e.target.value }))}
                        placeholder={tr('ex. Route du Dépôt 5, 1228 Plan-les-Ouates')}
                        style={{ ..._inp, textTransform: 'none' }}
                      />
                      <input
                        aria-label={tr('Téléphone du fournisseur')}
                        value={fiche.telephone || ''}
                        onChange={(e) => setFiche((v) => ({ ...v, telephone: e.target.value }))}
                        placeholder={tr('Téléphone (facultatif)')}
                        style={{ ..._inp, textTransform: 'none' }}
                      />
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <Btn ghost full label={tr('Annuler')} onClick={() => setFiche(null)} />
                        <Btn
                          full
                          color={C.green}
                          label={'✓ ' + tr('Enregistrer')}
                          onClick={async () => {
                            if (!String(fiche.nom || '').trim()) return;
                            try {
                              if (fiche.id) await bonsAchatApi.modifieFournisseur(fiche.id, fiche);
                              else await bonsAchatApi.ajouteFournisseur(fiche);
                              setFiche(null);
                              chargeCarnet();
                            } catch (e) {
                              setMessage(
                                e?.message || tr('Le fournisseur n’a pas pu être enregistré.'),
                              );
                            }
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 8 }}>
                      <Btn
                        sm
                        ghost
                        color={C.green}
                        label={'＋ ' + tr('Ajouter un fournisseur')}
                        onClick={() => setFiche({ nom: '', adresse: '', telephone: '' })}
                      />
                    </div>
                  )}
                </div>
              )}
            </Card>
            {visibles.map((b) => (
              <Card key={b.id} style={{ padding: '12px 13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 900, color: C.ink }}>
                      {b.numero} · {b.employeNom || '—'}
                    </div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                      {b.fournisseur} · {b.chantierNom || tr('Magasin')}
                      {b.offreNumero ? ' · ' + trf('Offre n° {0}', b.offreNumero) : ''}
                    </div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{suivi(b)}</div>
                  </div>
                  <Pastille bon={b} />
                </div>
                <div style={{ fontSize: 11.5, color: C.ink, marginTop: 6, lineHeight: 1.6 }}>
                  {(b.lignes || []).map((l, i) => (
                    <div key={i} style={l.etat === 'MANQUANT' ? { color: C.red } : undefined}>
                      {l.etat === 'MANQUANT' ? '✗ ' : l.etat === 'PRIS' ? '✓ ' : '• '}
                      <span
                        style={
                          l.etat === 'MANQUANT' ? { textDecoration: 'line-through' } : undefined
                        }
                      >
                        {[quantiteLisible(l), l.designation].filter(Boolean).join(' ')}
                      </span>
                      {l.etat === 'MANQUANT' ? ' — ' + tr('manquant chez le fournisseur') : ''}
                      {estPartiel(l) && (
                        <span style={{ color: C.amber, fontWeight: 700 }}>
                          {' — ' + tr('pris en partie, le reste manquait')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  <Btn sm ghost label={'🖨 ' + tr('Imprimer / PDF')} onClick={() => setFeuille(b)} />
                  {b.statut === 'ETABLI' && (
                    <Btn
                      sm
                      color={C.green}
                      label={'📲 ' + tr('Envoyer à') + ' ' + (b.employeNom || '').split(' ')[0]}
                      onClick={() => envoie(b)}
                    />
                  )}
                  {b.statut !== 'ACHETE' && (
                    <Btn
                      sm
                      ghost
                      label={'✎ ' + tr('Modifier')}
                      onClick={() => {
                        setMessage('');
                        setForm(dtoVersBon(b));
                      }}
                    />
                  )}
                  <Btn sm ghost danger label={'🗑 ' + tr('Supprimer')} onClick={() => supprime(b)} />
                </div>
              </Card>
            ))}
          </>
        )}
      </div>
      {feuille && <BonAchatA4 bon={feuille} soc={soc} onClose={() => setFeuille(null)} />}
    </Shell>
  );
}
